import React, { useCallback, useId } from 'react'

import { AIGenerated, Button, Check, Icon, InfoOutlined, Tooltip } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import formatPrice from 'Utils/formatPrice'
import { NOOP } from 'Utils/stableDefaults'

import ExplainerBanner from '../ExplainerBanner/ExplainerBanner'

const TRANSLATION_KEY_PREFIX = 'subscription_upgrade.order_summary'

function OrderSummary({
  premiumPrice,
  discount = 0,
  downloadsUsed,
  unusedCredits,
  amountDueNow,
  vatAmount = null,
  premiumPlan = null,
  onPay = NOOP,
  disabled = false,
  locale = 'en',
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale],
  )

  const vatTooltipId = useId()

  const showOriginalPrice = Boolean(premiumPlan?.originalPrice)
    && premiumPlan.originalPrice !== premiumPlan.currentPrice

  const legal = (
    <>
      {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_prefix`)}{' '}
      <a
        className="cgt-link-primary-accented"
        href="https://www.cgtrader.com/pages/terms-and-conditions"
        target="_blank"
        rel="noreferrer"
      >
        {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_terms`)}
      </a>
      ,{' '}
      <a
        className="cgt-link-primary-accented"
        href="https://www.cgtrader.com/pages/privacy-policy"
        target="_blank"
        rel="noreferrer"
      >
        {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_privacy`)}
      </a>
      {', '}{translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_connector`)}{' '}
      <a
        className="cgt-link-primary-accented"
        href="https://www.cgtrader.com/pages/cgtrader-subscription-terms"
        target="_blank"
        rel="noreferrer"
      >
        {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_fair_use`)}
      </a>
      .
    </>
  )

  return (
    <div className="order-summary">
      <h2 className="order-summary__title cgt-heading-300">
        {translate(`${TRANSLATION_KEY_PREFIX}.title`)}
      </h2>

      <ExplainerBanner downloadsUsed={downloadsUsed} unusedCredits={unusedCredits} locale={locale} />

      {premiumPlan && (
        <div className="order-summary__plan">
          <div className="order-summary__plan-content">
            <span className="order-summary__plan-name cgt-body-base-semibold">
              {translate(`${TRANSLATION_KEY_PREFIX}.plan_name`)}
              <Icon svgComponent={AIGenerated} size="small" notSpaced />
            </span>
            <span className="order-summary__plan-cadence cgt-caption">
              {translate(`${TRANSLATION_KEY_PREFIX}.plan_cadence`)}
            </span>
            <ul className="order-summary__features">
              {(premiumPlan.features || []).map((feature) => (
                <li key={feature} className="order-summary__feature">
                  <Icon svgComponent={Check} size="small" notSpaced />
                  <span className="order-summary__feature-text cgt-caption">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="order-summary__plan-pricing">
            <span className="order-summary__billing-period cgt-caption-semibold">
              {translate(`${TRANSLATION_KEY_PREFIX}.billing_period`)}
            </span>
            <div className="order-summary__price-wrap">
              {showOriginalPrice && (
                <span className="order-summary__original-price cgt-body-base-semibold">
                  {formatPrice(premiumPlan.originalPrice)}
                </span>
              )}
              <span className="order-summary__current-price cgt-body-lg-semibold">
                {formatPrice(premiumPlan.currentPrice)}
              </span>
            </div>
            <span className="order-summary__price-unit cgt-caption-semibold">
              {premiumPlan.priceUnit}
            </span>
          </div>
        </div>
      )}

      <hr className="order-summary__divider" />

      <div className="order-summary__totals">
        <div className="order-summary__row order-summary__row--premium-price">
          <span className="cgt-body-base-semibold">
            {translate(`${TRANSLATION_KEY_PREFIX}.premium_price`)}
          </span>
          <span className="cgt-body-base">{formatPrice(premiumPrice)}</span>
        </div>

        <div className="order-summary__row order-summary__row--credit">
          <span className="order-summary__credit-label cgt-body-base">
            {translate(`${TRANSLATION_KEY_PREFIX}.credit_label`)}
            <span className="order-summary__credit-basis cgt-caption">
              {translate(`${TRANSLATION_KEY_PREFIX}.credit_basis`, {
                unused: unusedCredits,
                limit: unusedCredits + downloadsUsed,
              })}
            </span>
          </span>
          <span className="order-summary__credit-amount cgt-body-base">
            {`-${formatPrice(discount)}`}
          </span>
        </div>

        {vatAmount > 0 && (
          <div className="order-summary__row order-summary__row--vat">
            <span className="order-summary__row-label--with-info cgt-body-base">
              {translate(`${TRANSLATION_KEY_PREFIX}.vat_included`)}
              <Tooltip content={translate(`${TRANSLATION_KEY_PREFIX}.vat_tooltip`)} id={vatTooltipId}>
                <span className="order-summary__info-icon">
                  <Icon size="small" svgComponent={InfoOutlined} color="dark" />
                </span>
              </Tooltip>
            </span>
            <span className="cgt-body-base">{formatPrice(vatAmount)}</span>
          </div>
        )}
      </div>

      <hr className="order-summary__divider" />

      <div className="order-summary__total-row">
        <span className="cgt-heading-50">
          {translate(`${TRANSLATION_KEY_PREFIX}.amount_due_today`)}
        </span>
        <span className="order-summary__total-amount cgt-heading-200">
          {formatPrice(amountDueNow)}
        </span>
      </div>

      <Button
        type="primary"
        expand
        actionType="button"
        customClass="order-summary__pay-button"
        onClick={onPay}
        disabled={disabled}
      >
        {translate(`${TRANSLATION_KEY_PREFIX}.cta`, { amount: formatPrice(amountDueNow) })}
      </Button>

      <div className="order-summary__legal cgt-body-base">{legal}</div>

      <p className="order-summary__footer cgt-caption">
        {translate(`${TRANSLATION_KEY_PREFIX}.next_billing_note`, { price: formatPrice(premiumPrice) })}
      </p>
    </div>
  )
}

OrderSummary.propTypes = {
  premiumPrice: number.isRequired,
  discount: number,
  downloadsUsed: number.isRequired,
  unusedCredits: number.isRequired,
  amountDueNow: number.isRequired,
  vatAmount: number,
  premiumPlan: shape({
    features: arrayOf(string),
    originalPrice: number,
    currentPrice: number,
    priceUnit: string,
  }),
  onPay: func,
  disabled: bool,
  locale: string,
}

export default OrderSummary
